import React, { useState, useCallback, useContext } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { NavigationNavbar } from '../components/navigation/NavigationNavbar';
import { navigateToLocaleRoot } from '../utils/routing/navigateToLocaleRoot';
import { useJobIslandContext } from '../contexts/JobIslandContext';
import { AuthContext } from '../contexts/AuthContext';
import type { SessionUser } from '../types/auth';

const MobileMenuModal = React.lazy(() =>
  import('../components/navigation/MobileMenuModal').then((m) => ({ default: m.MobileMenuModal }))
);

/** Resolve user for navbar: use AuthContext when it has a user; otherwise fall back to pageContext/jobIsland so avatar shows from SSR or before AuthProvider sync. */
function useNavbarUser(): SessionUser | null {
  const authContext = useContext(AuthContext);
  const jobIsland = useJobIslandContext();
  const pageContext = usePageContext();
  const fromAuth = authContext !== undefined ? authContext.user ?? null : null;
  const fromPage = (jobIsland?.user ?? (pageContext as any)?.user ?? null) as SessionUser | null;
  return fromAuth ?? fromPage;
}

/** Lightweight chrome: nav bar + mobile menu, no SearchProvider/Redux. Uses JobIslandContext when mounted in job island. */
export function MinimalChrome({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const pageContext = usePageContext();
  const jobIsland = useJobIslandContext();
  const user = useNavbarUser();
  const pathname =
    jobIsland?.pathname ??
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const urlOriginal =
    jobIsland?.urlOriginal ??
    (pageContext as any)?.urlOriginal ??
    (typeof window !== 'undefined' ? window.location.href : pathname);
  const fullUrlContent = urlOriginal.startsWith('http') ? urlOriginal : pathname;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const clearSearch = useCallback(async () => {
    await navigateToLocaleRoot(intl.locale);
  }, [intl.locale]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <header role="banner">
        <NavigationNavbar
          intl={intl}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          clearSearch={clearSearch}
          user={user}
        />
      </header>
      <div className="flex-1 flex lg:flex-row lg:relative overflow-hidden min-h-0">
        <React.Suspense fallback={null}>
          <MobileMenuModal
            isOpen={isMobileMenuOpen}
            onRequestClose={() => setIsMobileMenuOpen(false)}
            onCloseComplete={() => setIsMobileMenuOpen(false)}
            fullUrlContent={fullUrlContent}
            intl={intl}
          />
        </React.Suspense>
        <main
          className={`flex-1 overflow-x-hidden overflow-y-auto min-h-0 pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8 transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'lg:ml-[420px]' : 'lg:ml-0'
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
